import { useSuspenseQueries } from '@tanstack/react-query';
import React from 'react';
import { DomainViewAuthorizationService } from '../api/authorization/domain-view-authorization-service';
import {
  ExecutorRoleViewAuthorizationService,
  ExecutorRoleViewAuthorizationServiceImpl,
} from '../api/authorization/service';
import { UserViewDashboardService } from '../api/dashboards/user-view-dashboard-service';
import { useOldestStartDateQuery } from '../api/data/hooks';
import { BackendDataService, DataService } from '../api/data/service';
import { BackendEmployeeService } from '../api/employee/service';
import { Hasher } from '../api/hasher';
import { useExecutorRoleAllPermissionsQuery } from '../api/permission/hooks';
import { AssignedPermissions, Permission } from '../api/permission/types';
import { AuthenticatedGraphQLBackendService } from '../api/service';
import {
  BackendUserRolePreferencesService,
  UserRolePreferencesService,
} from '../api/userrolepreferences/service';
import { UserRolePreferencesSettings } from '../api/userrolepreferences/types';
import { Filter } from '../common/components/filter/filterbar/types';
import {
  BackendServiceContext,
  DataServiceContext,
  EmployeeServiceContext,
  ExecutorRolePermissionsContext,
  ExecutorRoleViewAuthorizationServiceContext,
  OldestStartDateContext,
  useAvailableDashboardsContext,
  useDomainContext,
  useDomainViewDashboardServiceContext,
  useEffectiveRoleContext,
  useExecutorRoleContext,
  useGraphQlRequestServiceContext,
  useLatestEmployeeVersionContext,
  useLatestSettingsContext,
  usePermissionServiceContext,
  UserRolePreferencesServiceContext,
  UserRolePreferencesSettingsContext,
  UserViewDashboardServiceContext,
  useSimulatedRoleContext,
} from './contexts';
import { useMemoWithDeps } from './hooks';

const DebugPrefix = 'ExecutorRoleScopedContextProvider';

const PreDependenciesLoader = ({
  children,
}: {
  children: (
    executorRolePermissions: AssignedPermissions<Permission>,
    dataService: DataService,
    userRolePreferences: UserRolePreferencesSettings<Filter> | null,
    userRolePreferenceFilterService: UserRolePreferencesService<Filter>,
    oldestStartDate: string
  ) => React.ReactNode;
}) => {
  const executorRole = useExecutorRoleContext();
  const domain = useDomainContext();
  const permissionService = usePermissionServiceContext();
  const graphQlRequestService = useGraphQlRequestServiceContext();
  const effectiveRoleId = useEffectiveRoleContext();
  const simulatedRole = useSimulatedRoleContext();
  const latestEmployeeVersion = useLatestEmployeeVersionContext();

  const executorRolePermissionsQuery = useExecutorRoleAllPermissionsQuery(
    `${DebugPrefix} - executorRoleAllPermissions`,
    permissionService,
    domain,
    executorRole
  );

  const userRolePreferenceFilterService = useMemoWithDeps(
    `${DebugPrefix} - userRolePreferenceFilterService`,
    () =>
      new BackendUserRolePreferencesService<Filter>(
        graphQlRequestService,
        effectiveRoleId,
        simulatedRole
      ),
    [effectiveRoleId, Hasher.hash(simulatedRole)],
    [graphQlRequestService]
  );

  const key = [
    'userRolePreferences',
    ...userRolePreferenceFilterService.getDeps(),
  ];
  const userRolePreferencesQuery = {
    queryKey: key,
    queryFn: () => {
      console.debug(`[useQuery] ${DebugPrefix} - userRolePreferences`, key);
      return userRolePreferenceFilterService.getLatestVersion();
    },
  };

  const dataService: DataService = useMemoWithDeps(
    `${DebugPrefix} - dataService domain=${domain}, executorRole=${
      executorRole.id
    }, simulatedRole=${simulatedRole?.id ?? 'null'}`,
    () =>
      new BackendDataService(
        domain,
        graphQlRequestService,
        executorRole,
        simulatedRole
      ),
    [domain, Hasher.hash(executorRole), Hasher.hash(simulatedRole)],
    [graphQlRequestService]
  );

  const oldestStartDateQuery = useOldestStartDateQuery(
    `${DebugPrefix} - oldestStartDate`,
    latestEmployeeVersion,
    dataService
  );

  const results = useSuspenseQueries({
    queries: [
      oldestStartDateQuery,
      userRolePreferencesQuery,
      executorRolePermissionsQuery,
    ],
  });

  const [oldestStartDate, userRolePreferences, executorRolePermissions]: [
    string | undefined,
    UserRolePreferencesSettings<Filter> | null,
    AssignedPermissions<Permission> | undefined,
  ] = [results[0].data, results[1].data ?? null, results[2].data];

  if (!executorRolePermissions) {
    throw new Error('Executor role permissions not found');
  }

  if (!oldestStartDate) {
    throw new Error('Oldest start date not found');
  }

  return (
    <>
      {children(
        executorRolePermissions,
        dataService,
        userRolePreferences ?? null,
        userRolePreferenceFilterService,
        oldestStartDate
      )}
    </>
  );
};

const ExecutorRoleScopedContextProviderInner = ({
  children,
  executorRolePermissions,
  oldestStartDate,
  dataService,
  userRolePreferences,
  userRolePreferenceFilterService,
}: {
  children: React.ReactNode;
  executorRolePermissions: AssignedPermissions<Permission>;
  oldestStartDate: string;
  dataService: DataService;
  userRolePreferences: UserRolePreferencesSettings<Filter> | null;
  userRolePreferenceFilterService: UserRolePreferencesService<Filter>;
}) => {
  const executorRole = useExecutorRoleContext();
  const domain = useDomainContext();
  const simulatedRole = useSimulatedRoleContext();
  const availableDashboards = useAvailableDashboardsContext();
  const graphQlRequestService = useGraphQlRequestServiceContext();
  const latestSettings = useLatestSettingsContext();
  const domainViewDashboardService = useDomainViewDashboardServiceContext();

  if (!executorRole) {
    // ExecutorRoleScopedContextProvider can only wrap part of the app where executor role is present
    throw new Error('Executor Role not found');
  }

  const domainViewAuthorizationService = useMemoWithDeps(
    `${DebugPrefix} - domainViewAuthorizationService`,
    () => new DomainViewAuthorizationService(domain, latestSettings),
    [domain, Hasher.hash(latestSettings)]
  );

  const backendService = useMemoWithDeps(
    `${DebugPrefix} - backendService domain=${domain}, executorRole=${
      executorRole.id
    }, simulatedRole=${simulatedRole?.id ?? 'null'}`,
    () =>
      new AuthenticatedGraphQLBackendService(
        graphQlRequestService,
        domain,
        executorRole,
        simulatedRole
      ),
    [domain, Hasher.hash(executorRole), Hasher.hash(simulatedRole)],
    [graphQlRequestService]
  );

  const employeeService = useMemoWithDeps(
    `${DebugPrefix} - employeeService`,
    () => new BackendEmployeeService(dataService, backendService, domain),
    [domain],
    [dataService, backendService]
  );

  const authorizationService: ExecutorRoleViewAuthorizationService =
    useMemoWithDeps(
      `${DebugPrefix} - authorizationService`,
      () =>
        new ExecutorRoleViewAuthorizationServiceImpl(
          domain,
          domainViewAuthorizationService,
          executorRolePermissions,
          availableDashboards
        ),
      [
        domain,
        Hasher.hash(executorRolePermissions),
        Hasher.hash(availableDashboards),
      ],
      [domainViewAuthorizationService]
    );

  const userViewDashboardService: UserViewDashboardService = useMemoWithDeps(
    `${DebugPrefix} - userViewDashboardService`,
    () =>
      new UserViewDashboardService(
        domainViewDashboardService,
        authorizationService
      ),
    [],
    [domainViewDashboardService, authorizationService]
  );

  const userRolePreferencesServiceFilterContext =
    UserRolePreferencesServiceContext<Filter>();

  return (
    <ExecutorRolePermissionsContext.Provider value={executorRolePermissions}>
      <userRolePreferencesServiceFilterContext.Provider
        value={userRolePreferenceFilterService}
      >
        <UserViewDashboardServiceContext.Provider
          value={userViewDashboardService}
        >
          <UserRolePreferencesSettingsContext.Provider
            value={userRolePreferences}
          >
            <OldestStartDateContext.Provider value={oldestStartDate}>
              <DataServiceContext.Provider value={dataService}>
                <BackendServiceContext.Provider value={backendService}>
                  <ExecutorRoleViewAuthorizationServiceContext.Provider
                    value={authorizationService}
                  >
                    <EmployeeServiceContext.Provider value={employeeService}>
                      {children}
                    </EmployeeServiceContext.Provider>
                  </ExecutorRoleViewAuthorizationServiceContext.Provider>
                </BackendServiceContext.Provider>
              </DataServiceContext.Provider>
            </OldestStartDateContext.Provider>
          </UserRolePreferencesSettingsContext.Provider>
        </UserViewDashboardServiceContext.Provider>
      </userRolePreferencesServiceFilterContext.Provider>
    </ExecutorRolePermissionsContext.Provider>
  );
};

export const ExecutorRoleScopedContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return (
    <PreDependenciesLoader>
      {(
        executorRolePermissions,
        dataService,
        userRolePreferences,
        userRolePreferenceFilterService,
        oldestStartDate
      ) => (
        <ExecutorRoleScopedContextProviderInner
          executorRolePermissions={executorRolePermissions}
          oldestStartDate={oldestStartDate}
          dataService={dataService}
          userRolePreferences={userRolePreferences}
          userRolePreferenceFilterService={userRolePreferenceFilterService}
        >
          {children}
        </ExecutorRoleScopedContextProviderInner>
      )}
    </PreDependenciesLoader>
  );
};
